import React, { useEffect } from 'react';
import clamp from 'lodash/clamp';
import { useParams } from 'react-router-dom';
import { DEFAULT_COLLECTION_ID } from '../../data-layer/collections/service';
import { COLLECTIONS_NAMESPACE_PREFIX } from '../../data-layer/posts/reducer';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconArrowLeft } from '../../ui/icon/IconArrowLeft';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { InfiniteList } from '../../ui/layout/InfiniteList';
import { calculateHeight, PostCard, PostCardSkeleton } from '../../ui/posts/PostCard/PostCard';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { ToolbarIconButton } from '../../ui/surface/Toolbar';
import { Typography } from '../../ui/typography/Typography';
import { CollectionMenu } from '../../ui/users/CollectionMenu';
import { EventSource } from '../../utils/analyticsService';
import { BREAKPOINTS } from '../../utils/styles/breakpoints';
import { useBackButton } from '../../utils/useBackButton';
import { usePostCollection } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { withAuthProtection } from '../withAuthProtection';
import { useCollection } from './useCollection';
import { useCurrentUser } from './useCurrentUser';
import { withCollectionCallbacks } from './withCollectionCallbacks';

const EnhancedCollectionMenu = withCollectionCallbacks(CollectionMenu);
const EnhancedPostCard = withPostCallbacks(PostCard, EventSource.COLLECTION);
const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.COLLECTION);

const PER_PAGE = 12;

export const CollectionDetails = withAuthProtection(() => {
    const params = useParams();
    const collectionId = params.collectionId!;
    const onBack = useBackButton();
    const collectionModel = useCollection({ collectionId });
    const postCollection = usePostCollection({
        namespace: `${COLLECTIONS_NAMESPACE_PREFIX}${collectionId}`,
        filters: { collectionId, amount: PER_PAGE },
    });
    const currentUser = useCurrentUser();

    useEffect(() => postCollection.onReset, [postCollection.onReset]);

    return (
        <DashboardPage>
            <Flex width="100%" mt={{ xs: 0, sm: -2, md: -3 }} spacing={{ sm: 1 }}>
                <Flex
                    direction="row"
                    alignItems="center"
                    p={1}
                    width="100%"
                    borderBottomLeftRadius={{ sm: 2 }}
                    borderBottomRightRadius={{ sm: 2 }}
                    background="var(--background-primary)"
                >
                    <ToolbarIconButton onClick={onBack}>
                        <IconArrowLeft />
                    </ToolbarIconButton>
                    {collectionModel?.displayName && (
                        <Typography variant="title3" weight="bold" width="100%" textAlign="center">
                            {collectionModel.displayName}
                        </Typography>
                    )}
                    {collectionModel && collectionId !== DEFAULT_COLLECTION_ID && (
                        <EnhancedCollectionMenu collection={collectionModel} />
                    )}
                </Flex>
                <InfiniteList
                    perPage={PER_PAGE}
                    count={postCollection.items.length}
                    isLoading={postCollection.isLoading}
                    isEndReached={postCollection.isEndReached}
                    onLoadNext={postCollection.onLoadNext}
                    estimatedRowSize={(width) => clamp((width * 16) / 9, 0, window.innerHeight)}
                    rowHeight={(width, index) => {
                        const item = postCollection.items[index];
                        const height = calculateHeight(width, item?.content);

                        return height + (window.innerWidth < BREAKPOINTS.sm ? 0 : 16);
                    }}
                    renderItem={(index) => {
                        const item = postCollection.items[index];

                        return (
                            <Box width="100%">
                                {item ? (
                                    <EnhancedPostCard
                                        post={item}
                                        index={index}
                                        collectionId={collectionId}
                                        renderMenu={(trigger) => (
                                            <EnhancedPostContextMenu
                                                trigger={trigger}
                                                post={item}
                                                user={currentUser}
                                            />
                                        )}
                                    />
                                ) : (
                                    <PostCardSkeleton />
                                )}
                            </Box>
                        );
                    }}
                />
            </Flex>
        </DashboardPage>
    );
});
